/**
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): reconciliation decisions need uploadDocuments, proposal override needs reviewProposals (server permission map)
 * DataQuality.tsx
 * v4.09 - 2026-04-26 - WRANGLING-L3: Resolution state summary banner (auto-resolved / needs review / escalated)
 * v4.06 - 2026-04-26 - [174]/[92]: Source Trust Panel, Resolution Log, Conflict Queue, Cleaning Summary
 *
 * Four tabs:
 *   1. Source Trust      — view + edit per-connector trust rank overrides
 *   2. Resolution Log    — paginated cross-source resolution audit trail
 *   3. Conflict Queue    — pending Tier-3 conflicts awaiting user decision
 *   4. Cleaning Summary  — plain-English Silver transform statistics ([92])
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Slider,
  Snackbar,
  TablePagination,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import { CheckCircle, Cancel, HelpOutline, Build as CleaningServices } from '@mui/icons-material';
import TopBar from '../components/TopBar';
import { trustService, api } from '../services/api';
import { useClerkAuth } from '../App';
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B

// ── Tab panel helper ──────────────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);


// ─────────────────────────────────────────────────────────────────────────────
// Tab 1 — Source Trust
// ─────────────────────────────────────────────────────────────────────────────

interface TrustRankRow {
  connector: string;
  rank: number;
  is_override: boolean;
  updated_at: string | null;
}

const SourceTrustTab: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [rows, setRows] = useState<TrustRankRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRanks, setPendingRanks] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const fetchRanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trustService.getRanks(tenantId);
      setRows(data.ranks);
      const initial: Record<string, number> = {};
      data.ranks.forEach((r) => { initial[r.connector] = r.rank; });
      setPendingRanks(initial);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load trust ranks.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchRanks(); }, [fetchRanks]);

  const handleSave = async (connector: string) => {
    setSaving(connector);
    try {
      await trustService.updateRank(tenantId, connector, pendingRanks[connector]);
      setSnackbar({ open: true, msg: `Trust rank for ${connector} updated.`, severity: 'success' });
      // Refresh to reflect is_override flag change
      fetchRanks();
    } catch (err: any) {
      setSnackbar({ open: true, msg: err?.response?.data?.detail || 'Failed to save.', severity: 'error' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (rows.length === 0) return (
    <Alert severity="info">
      No connector data yet — run the pipeline to populate trust ranks.
    </Alert>
  );

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Lower rank number = higher trust. Rank 1 wins; rank 10 loses. Overrides apply to this tenant only.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Connector</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Effective Rank</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 220 }}>Override</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const current = pendingRanks[row.connector] ?? row.rank;
              const changed = current !== row.rank;
              return (
                <TableRow key={row.connector} hover>
                  <TableCell sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{row.connector}</TableCell>
                  <TableCell>{row.rank}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Slider
                        value={current}
                        min={1}
                        max={10}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        onChange={(_, val) => setPendingRanks((p) => ({ ...p, [row.connector]: val as number }))}
                        sx={{ width: 140 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 16 }}>{current}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.is_override ? (
                      <Chip label="Custom" size="small" color="primary" variant="outlined" />
                    ) : (
                      <Chip label="Default" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant={changed ? 'contained' : 'outlined'}
                      disabled={!changed || saving === row.connector}
                      onClick={() => handleSave(row.connector)}
                      startIcon={saving === row.connector ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.msg}
      />
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — Resolution Log
// ─────────────────────────────────────────────────────────────────────────────

const RESOLUTION_LABELS: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  suppressed:     { label: 'Suppressed',     color: 'warning' },
  conflict_queue: { label: 'Conflict Queue', color: 'default' },
  no_conflict:    { label: 'No Conflict',    color: 'success' },
};

const ResolutionLogTab: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [connectorFilter, setConnectorFilter] = useState('');
  const [tierFilter, setTierFilter] = useState<number | ''>('');
  const [resolutionFilter, setResolutionFilter] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit: rowsPerPage, offset: page * rowsPerPage };
      if (connectorFilter) params.connector_name = connectorFilter;
      if (tierFilter !== '') params.match_tier = tierFilter;
      if (resolutionFilter) params.resolution = resolutionFilter;
      const data = await trustService.getResolutionLog(tenantId, params);
      setRows(data.rows);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load resolution log.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, rowsPerPage, connectorFilter, tierFilter, resolutionFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return (
    <>
      {/* Filter row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Connector"
          value={connectorFilter}
          onChange={(e) => { setConnectorFilter(e.target.value); setPage(0); }}
          sx={{ width: 160 }}
        />
        <FormControl size="small" sx={{ width: 140 }}>
          <InputLabel>Match Tier</InputLabel>
          <Select
            value={tierFilter}
            label="Match Tier"
            onChange={(e) => { setTierFilter(e.target.value as number | ''); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>1 — Exact key</MenuItem>
            <MenuItem value={2}>2 — Composite</MenuItem>
            <MenuItem value={3}>3 — Unresolvable</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Resolution</InputLabel>
          <Select
            value={resolutionFilter}
            label="Resolution"
            onChange={(e) => { setResolutionFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="suppressed">Suppressed</MenuItem>
            <MenuItem value="conflict_queue">Conflict Queue</MenuItem>
            <MenuItem value="no_conflict">No Conflict</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" onClick={() => { setConnectorFilter(''); setTierFilter(''); setResolutionFilter(''); setPage(0); }}>
          Clear
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rectangular" height={300} />
      ) : rows.length === 0 ? (
        <Alert severity="info">No resolution events found for the selected filters.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entity Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Winner</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Loser</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Resolution</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Business Key</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const res = RESOLUTION_LABELS[row.resolution] || { label: row.resolution, color: 'default' as const };
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.entity_type}</TableCell>
                    <TableCell>
                      <Chip label={`${row.source_winner} (${row.winner_rank})`} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={`${row.source_loser} (${row.loser_rank})`} size="small" color="warning" variant="outlined" />
                    </TableCell>
                    <TableCell>{row.match_tier}</TableCell>
                    <TableCell>
                      <Chip label={res.label} size="small" color={res.color} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.conflict_fields?.join(', ') || ''} placement="top">
                        <span>{row.business_key || '—'}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Conflict Queue
// ─────────────────────────────────────────────────────────────────────────────

interface ConflictItem {
  decision_id: string;
  source_a_ref: Record<string, any>;
  source_b_ref: Record<string, any>;
  match_signals: Record<string, any>;
  match_tier: number;
  queued_at: string | null;
}

const ConflictQueueTab: React.FC = () => {
  const { hasPermission } = usePermissions(); // v4.28 - G10-B: deciding a reconciliation follows uploadDocuments
  const canDecideReconciliation = hasPermission('uploadDocuments');
  const [items, setItems] = useState<ConflictItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/ingest/document/reconciliation-queue', { params: { limit: 50, offset: 0 } });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load conflict queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleDecide = async (decisionId: string, decision: 'same' | 'different') => {
    setDeciding(decisionId);
    setError(null);
    try {
      await api.post(`/ingest/document/reconciliation-queue/${decisionId}`, { decision });
      setItems((prev) => prev.filter((i) => i.decision_id !== decisionId));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to resolve conflict.');
    } finally {
      setDeciding(null);
    }
  };

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (items.length === 0) return (
    <Alert severity="success" icon={<CheckCircle />}>
      No pending conflicts — all cross-source differences have been automatically resolved.
    </Alert>
  );

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {total} pending conflict{total !== 1 ? 's' : ''} require your decision. "Same Field" marks the records as duplicates; "Different Field" keeps both.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Queued</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source A</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source B</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Match Signals</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.decision_id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {item.queued_at ? new Date(item.queued_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{item.match_tier}</TableCell>
                <TableCell>
                  <Typography variant="caption" component="div">
                    {item.source_a_ref?.connector_type || item.source_a_ref?.source_type || 'connector'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.source_a_ref?.record_id || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" component="div">
                    {item.source_b_ref?.source_type || item.source_b_ref?.connector_type || 'document'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.source_b_ref?.record_id || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {Object.entries(item.match_signals || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  {canDecideReconciliation && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={deciding === item.decision_id ? <CircularProgress size={12} color="inherit" /> : <CheckCircle />}
                      disabled={deciding === item.decision_id}
                      onClick={() => handleDecide(item.decision_id, 'same')}
                    >
                      Same
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<Cancel />}
                      disabled={deciding === item.decision_id}
                      onClick={() => handleDecide(item.decision_id, 'different')}
                    >
                      Different
                    </Button>
                  </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Tab 4 — Cleaning Summary ([92])
// ─────────────────────────────────────────────────────────────────────────────

const CleaningSummaryTab: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [data, setData] = useState<{
    stats: { technique_id: string; label: string; count: number }[];
    pipeline_run_id: string | null;
    started_at: string | null;
    total_rows: number;
    message: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await trustService.getCleaningSummary(tenantId);
        setData(res);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load cleaning summary.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantId]);

  if (loading) return <Skeleton variant="rectangular" height={240} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data || data.stats.length === 0) return (
    <Alert severity="info">
      {data?.message || 'Run the pipeline to see cleaning statistics.'}
    </Alert>
  );

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Pipeline run: <strong>{data.pipeline_run_id || '—'}</strong>
        </Typography>
        {data.started_at && (
          <Typography variant="body2" color="text.secondary">
            Started: <strong>{new Date(data.started_at).toLocaleString()}</strong>
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Total rows processed: <strong>{data.total_rows.toLocaleString()}</strong>
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, mb: 4 }}>
        {data.stats.map((stat) => (
          <Card key={stat.technique_id} variant="outlined">
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CleaningServices sx={{ color: 'primary.main', mt: 0.3, fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{stat.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{stat.technique_id}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* [92] Raw vs Cleaned chart placeholder */}
      <Alert severity="info" icon={<HelpOutline />}>
        Field-level raw vs. cleaned comparison charts are coming in a future release.
        The statistics above reflect row-level counts from the Silver transform phase.
      </Alert>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Resolution State Banner (v4.09 - WRANGLING-L3)
// ─────────────────────────────────────────────────────────────────────────────

interface ResolutionSummary {
  auto_resolved: number;
  needs_review: number;
  escalated: number;
  total: number;
  run_id: string | null;
  as_of: string | null;
}

const ResolutionStateBanner: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [summary, setSummary] = useState<ResolutionSummary | null>(null);

  useEffect(() => {
    trustService.getProposalsSummary(tenantId)
      .then(setSummary)
      .catch(() => { /* non-fatal — banner simply stays hidden */ });
  }, [tenantId]);

  if (!summary || summary.total === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Trust decisions:
      </Typography>
      <Chip
        label={`${summary.auto_resolved} Auto-resolved`}
        size="small"
        sx={{ bgcolor: '#4CAF50', color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
      />
      <Chip
        label={`${summary.needs_review} Need review`}
        size="small"
        sx={{ bgcolor: '#2196F3', color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
      />
      <Chip
        label={`${summary.escalated} Escalated`}
        size="small"
        sx={{ bgcolor: '#FF9800', color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
      />
      {summary.as_of && (
        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
          as of {new Date(summary.as_of).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Tab — Trust Proposals (v4.15 - TRUST-PROPOSALS-UI)
// Per-proposal drill-down: confidence, resolution state, collusion flag, and a
// manual-override action. Companion to the aggregate ResolutionStateBanner.
// ─────────────────────────────────────────────────────────────────────────────

export const BUCKET_LABELS: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  auto_resolved: { label: 'Auto resolved', color: 'success' },
  needs_review:  { label: 'Needs review',  color: 'warning' },
  escalated:     { label: 'Escalated',     color: 'error' },
};

export const confidenceColor = (c: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (c === null || c === undefined) return 'default';
  if (c >= 0.75) return 'success';
  if (c >= 0.5) return 'warning';
  return 'error';
};

export const TrustProposalsTab: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { hasPermission } = usePermissions(); // v4.28 - G10-B: overriding a proposal follows reviewProposals
  const canReviewProposals = hasPermission('reviewProposals');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [bucketFilter, setBucketFilter] = useState('');
  const [overriding, setOverriding] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit: rowsPerPage, offset: page * rowsPerPage };
      if (bucketFilter) params.bucket = bucketFilter;
      const data = await trustService.getProposals(tenantId, params);
      setRows(data.rows);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load trust proposals.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, rowsPerPage, bucketFilter]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  const handleOverride = async (proposalId: number) => {
    setOverriding(proposalId);
    try {
      await trustService.overrideProposal(tenantId, proposalId);
      setRows((prev) => prev.map((r) => (r.id === proposalId ? { ...r, overridden: true, status: 'overridden' } : r)));
      setSnackbar({ open: true, msg: 'Proposal overridden.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, msg: err?.response?.data?.detail || 'Override failed.', severity: 'error' });
    } finally {
      setOverriding(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>Bucket</InputLabel>
          <Select
            value={bucketFilter}
            label="Bucket"
            onChange={(e) => { setBucketFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="auto_resolved">Auto resolved</MenuItem>
            <MenuItem value="needs_review">Needs review</MenuItem>
            <MenuItem value="escalated">Escalated</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" onClick={() => { setBucketFilter(''); setPage(0); }}>Clear</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rectangular" height={300} />
      ) : rows.length === 0 ? (
        <Alert severity="info">No trust proposals found for the selected filter.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Entity / Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Winning Source</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Collusion</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const bucket = BUCKET_LABELS[row.bucket] || BUCKET_LABELS.needs_review;
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                        {row.entity_type}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{row.field_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.confidence !== null ? row.confidence.toFixed(2) : '—'}
                        size="small"
                        color={confidenceColor(row.confidence)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell><Chip label={bucket.label} size="small" color={bucket.color} /></TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.winning_source || '—'}</TableCell>
                    <TableCell>
                      {row.collusion_detected
                        ? <Chip label="Detected" size="small" color="error" />
                        : <Typography variant="caption" color="text.disabled">None</Typography>}
                    </TableCell>
                    <TableCell>
                      {row.overridden ? (
                        <Chip label="Overridden" size="small" color="info" variant="outlined" />
                      ) : !canReviewProposals ? null : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={overriding === row.id}
                          onClick={() => handleOverride(row.id)}
                        >
                          {overriding === row.id ? <CircularProgress size={14} /> : 'Override'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Main DataQuality page
// ─────────────────────────────────────────────────────────────────────────────

const DataQuality: React.FC = () => {
  const { tenantId } = useClerkAuth();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <TopBar title="Data Quality" subtitle="Trust rankings, resolution history, and pipeline cleaning stats" />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* v4.09 - WRANGLING-L3: Resolution state summary */}
        {tenantId && <ResolutionStateBanner tenantId={tenantId} />}

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tab label="Source Trust" />
          <Tab label="Resolution Log" />
          <Tab label="Trust Proposals" />
          <Tab label="Conflict Queue" />
          <Tab label="Cleaning Summary" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {tenantId
            ? <SourceTrustTab tenantId={tenantId} />
            : <Alert severity="warning">Tenant not identified. Please refresh the page.</Alert>}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {tenantId
            ? <ResolutionLogTab tenantId={tenantId} />
            : <Alert severity="warning">Tenant not identified. Please refresh the page.</Alert>}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {tenantId
            ? <TrustProposalsTab tenantId={tenantId} />
            : <Alert severity="warning">Tenant not identified. Please refresh the page.</Alert>}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <ConflictQueueTab />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {tenantId
            ? <CleaningSummaryTab tenantId={tenantId} />
            : <Alert severity="warning">Tenant not identified. Please refresh the page.</Alert>}
        </TabPanel>
      </Container>
    </Box>
  );
};

export default DataQuality;
